type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

export type WebAuthnCredentialPayload = {
    id: string
    rawId: string
    type: string
    response: Record<string, JsonValue>
    authenticatorAttachment?: string | null
    clientExtensionResults: AuthenticationExtensionsClientOutputs
}

const base64UrlToArrayBuffer = (base64url: string): ArrayBuffer => {
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=')
    const binary = window.atob(padded)
    const bytes = new Uint8Array(binary.length)

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
    }

    return bytes.buffer
}

const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''

    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte)
    })

    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

const credentialDescriptorsToArrayBuffers = (
    descriptors?: PublicKeyCredentialDescriptor[]
): PublicKeyCredentialDescriptor[] | undefined => {
    return descriptors?.map((descriptor) => ({
        ...descriptor,
        id: typeof descriptor.id === 'string'
            ? base64UrlToArrayBuffer(descriptor.id)
            : descriptor.id,
    }))
}

export const preparePublicKeyCreationOptions = (
    options: PublicKeyCredentialCreationOptions | PublicKeyCredentialRequestOptions
): PublicKeyCredentialCreationOptions => {
    const creationOptions = options as PublicKeyCredentialCreationOptions

    return {
        ...creationOptions,
        challenge: typeof creationOptions.challenge === 'string'
            ? base64UrlToArrayBuffer(creationOptions.challenge)
            : creationOptions.challenge,
        user: {
            ...creationOptions.user,
            id: typeof creationOptions.user.id === 'string'
                ? base64UrlToArrayBuffer(creationOptions.user.id)
                : creationOptions.user.id,
        },
        excludeCredentials: credentialDescriptorsToArrayBuffers(creationOptions.excludeCredentials),
    }
}

export const preparePublicKeyRequestOptions = (
    options: PublicKeyCredentialCreationOptions | PublicKeyCredentialRequestOptions
): PublicKeyCredentialRequestOptions => {
    const requestOptions = options as PublicKeyCredentialRequestOptions

    return {
        ...requestOptions,
        challenge: typeof requestOptions.challenge === 'string'
            ? base64UrlToArrayBuffer(requestOptions.challenge)
            : requestOptions.challenge,
        allowCredentials: credentialDescriptorsToArrayBuffers(requestOptions.allowCredentials),
    }
}

export const publicKeyCredentialToPayload = (
    credential: Credential
): WebAuthnCredentialPayload => {
    const publicKeyCredential = credential as PublicKeyCredential
    const response: Record<string, JsonValue> = {
    }

    if (publicKeyCredential.response instanceof AuthenticatorAttestationResponse) {
        response.clientDataJSON = arrayBufferToBase64Url(publicKeyCredential.response.clientDataJSON)
        response.attestationObject = arrayBufferToBase64Url(publicKeyCredential.response.attestationObject)
    }

    if (publicKeyCredential.response instanceof AuthenticatorAssertionResponse) {
        response.clientDataJSON = arrayBufferToBase64Url(publicKeyCredential.response.clientDataJSON)
        response.authenticatorData = arrayBufferToBase64Url(publicKeyCredential.response.authenticatorData)
        response.signature = arrayBufferToBase64Url(publicKeyCredential.response.signature)
        response.userHandle = publicKeyCredential.response.userHandle
            ? arrayBufferToBase64Url(publicKeyCredential.response.userHandle)
            : null
    }

    return {
        id: publicKeyCredential.id,
        rawId: arrayBufferToBase64Url(publicKeyCredential.rawId),
        type: publicKeyCredential.type,
        response,
        authenticatorAttachment: publicKeyCredential.authenticatorAttachment ?? null,
        clientExtensionResults: publicKeyCredential.getClientExtensionResults(),
    }
}

export const assertWebAuthnSupported = () => {
    if (!window.PublicKeyCredential || !navigator.credentials) {
        throw new Error('Passkeys are not supported in this browser.')
    }
}
